import { Text, VStack } from "@chakra-ui/react";
import * as React from "react";
import { graphql, useFragment } from "react-relay";

import { GraphQLEnums } from "~/src/__generated__/GraphQLEnums";
import { ABTestDeliverDayOfWeeks_abtestScenarioPageGroup$key } from "~/src/__relay_artifacts__/ABTestDeliverDayOfWeeks_abtestScenarioPageGroup.graphql";
import { Chip } from "~/src/components/common/Chip";
import { DefinitionTeam } from "~/src/components/common/DefinitionTeam";
import { formatISOToTime } from "~/src/lib/utils/date-utils";

type Props = {
  abtestScenarioPageGroupRef: ABTestDeliverDayOfWeeks_abtestScenarioPageGroup$key;
};

const fragment = graphql`
  fragment ABTestDeliverDayOfWeeks_abtestScenarioPageGroup on AbtestScenarioPageGroup {
    startDate
    endDate
    deliverDayOfWeeks {
      weekOfMonth
      daysOfWeek
    }
    schedules {
      id
      startTime
      endTime
    }
  }
`;

export const ABTestDeliverDayOfWeeks: React.VFC<Props> = ({
  abtestScenarioPageGroupRef,
}) => {
  const abtestGroup = useFragment(fragment, abtestScenarioPageGroupRef);

  const dateDuration = React.useMemo(() => {
    if (abtestGroup.startDate == null && abtestGroup.endDate == null) return "";

    return `${abtestGroup.startDate || ""} ~ ${abtestGroup.endDate || ""}`;
  }, [abtestGroup.endDate, abtestGroup.startDate]);

  const deliverDayOfWeeks = React.useMemo(
    () =>
      abtestGroup.deliverDayOfWeeks.map((deliverDayOfWeek) => {
        const weekOfMonth = deliverDayOfWeek.weekOfMonth
          ? `第${deliverDayOfWeek.weekOfMonth}週`
          : "毎週";
        const daysOfWeek = deliverDayOfWeek.daysOfWeek.map(
          (dayOfWeek) =>
            GraphQLEnums.DayOfWeek.find(({ value }) => value === dayOfWeek)
              ?.label
        );
        return `${weekOfMonth} ${daysOfWeek.join("・")}`;
      }),
    [abtestGroup.deliverDayOfWeeks]
  );

  const schedules = React.useMemo(
    () =>
      (abtestGroup.schedules || [])
        .map((schedule) => {
          const startTime = formatISOToTime(schedule.startTime);
          const endTime = formatISOToTime(schedule.endTime);
          if (!startTime && !endTime) return null;

          return {
            label: `${startTime || ""} ~ ${endTime || ""}`,
            value: schedule.id,
          };
        })
        .filter(Boolean),
    [abtestGroup.schedules]
  );

  return (
    <>
      <DefinitionTeam label="実施期間" dense>
        {dateDuration}
      </DefinitionTeam>
      <DefinitionTeam label="実施時間" dense>
        {schedules.map((schedule) =>
          schedule ? <Chip key={schedule.value} label={schedule.label} /> : null
        )}
      </DefinitionTeam>
      <DefinitionTeam label="実施曜日" dense>
        <VStack alignItems={"flex-start"}>
          {deliverDayOfWeeks.map((deliverDayOfWeek, index) => (
            <Text key={index}>{deliverDayOfWeek}</Text>
          ))}
        </VStack>
      </DefinitionTeam>
    </>
  );
};
